<template>
	<v-container class="ghostDraftView">
		<v-row class="ghostDraftControls h-100 w-100">
			<v-col>
				<v-progress-linear v-if="isWaiting" indeterminate color="primary" height="4"></v-progress-linear>
				<div ref="dataCaptureControlContainer" class="datacapture-control-container"
					style="min-height: var(--app-size-ghostdraft-min-h) !important; display: none;"></div>
			</v-col>
		</v-row>
	</v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, inject, defineProps, nextTick, withDefaults, onUnmounted } from 'vue';
import { Tell } from '@/services/Tell';
import { PolicyFormDetails } from '@/models/PrintDocumentModel';
import { GhostDraftService } from '@/services/GhostDraftService';
import * as GhostDraftDataCapture from '@/assets/GhostDraftDataCapture-3.0';
import { EventBus, Events } from '@/contracts/EventBus';

interface Props {
	formDetails: PolicyFormDetails;
}

const props = withDefaults(defineProps<Props>(), {
	formDetails: () => new PolicyFormDetails()
});

const dataCaptureControlContainer = ref<HTMLDivElement | null>(null);
const dataCaptureOneTimeUseAuthToken = ref<string>('');
const isWaiting = ref<boolean>(true);
const dataCapture = ref<GhostDraftDataCapture.IDataCaptureComponent | null>(null);
const eventBus = inject('eventBus') as EventBus;
const tell = inject('tell') as Tell;

const updateRequested = async (currentForm: PolicyFormDetails) => {
	if(currentForm.formName === props.formDetails.formName)
	{
		//extract serverXml from the component, update the current instance, send it out to listeners 
		if(dataCapture.value)
		{
			await dataCapture.value.getXml().then((xml: string) => {
				if (xml) {
					// Update the serverXml in the formDetails
					currentForm.serverXml = xml;

					// TODO: figure out clean way to validate? do we care?

					// Emit the updated object to anyone who cares 
					eventBus.emit(Events.INTERACTIVE_FORMS_UPDATE_COMPLETE, currentForm);
				}
			}).catch((error: any) => {
				console.error('Error getting XML from Ghost Draft:', error);
				eventBus.emit(Events.INTERACTIVE_FORMS_UPDATE_COMPLETE, currentForm);
			});
		}
	}
};

const createDataCaptureUX = async () => {
	// serverXml will be encoded because it's stored as json in the database
	let serverXml = decodeURIComponent(props.formDetails?.serverXml || '');
	let args = {
		template: props.formDetails.ghostDraftFormName,
		company: props.formDetails.ghostDraftCompany,
		documentService: props.formDetails.ghostDraftDocumentServiceName, // Map document service
		packageName: props.formDetails.ghostDraftPackageName, // Map package name
		packageVersion: props.formDetails.ghostDraftPackageVersion, // Map package version
		serverXml: serverXml, // Decode server XML
	};

	// Create the data capture component
	const dataCaptureComponent = await GhostDraftDataCapture.createDataCapture({
		targetDiv: dataCaptureControlContainer.value,
		token: dataCaptureOneTimeUseAuthToken.value,
		baseUrl: undefined, // Reserved for future use
		template: args.template,
		dialogDesign: '',
		options:
			GhostDraftDataCapture.DataCaptureDisplayFlags.showTopBar |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showPreview |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showBottomBar |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showMenu |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showNextPreviousButtons |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showLeftPane |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showLogo |
			GhostDraftDataCapture.DataCaptureDisplayFlags.hideSubmit |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showSearch |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showPreviewOpen |
			GhostDraftDataCapture.DataCaptureDisplayFlags.showToolbar,
		company: args.company,
		documentService: args.documentService,
		packageName: args.packageName,
		packageVersion: args.packageVersion,
		serverXml: args.serverXml				// Note: Not passing serverXml here - will use populateForm() method instead
	});

	// Store the data capture component
	dataCapture.value = dataCaptureComponent;
}

function applyIframeStyles() {
	const container_2 = dataCaptureControlContainer.value;

	const containers = [container_2];
	containers.forEach(container => {
		if (!container) return;
		const iframes = container.querySelectorAll('iframe');
		iframes.forEach(iframe => {
			iframe.setAttribute('width', '100%');
			iframe.setAttribute('height', '100%');
		});
	});
}

onMounted(async () => {
	try {
		await nextTick();

		//we're not ready
		eventBus.emit(Events.INTERACTIVE_FORMS_DATACAPTURE_READY, false);

		dataCaptureOneTimeUseAuthToken.value = await GhostDraftService.getDatCaptureAuthToken();

		if (!dataCaptureControlContainer.value) {
			tell.error('Data capture container not found');
		}

		await createDataCaptureUX();

		applyIframeStyles();

		dataCaptureControlContainer.value!.style.display = 'block';

		//await nextTick(); // Wait for the DOM to update

		//observeIframes(dataCaptureControlContainer.value);
		isWaiting.value = false;
		
		eventBus.on(Events.INTERACTIVE_FORMS_UPDATE_REQUESTED, updateRequested);
		eventBus.emit(Events.INTERACTIVE_FORMS_DATACAPTURE_READY, true);
	} catch (error) {
		tell.error(`Error initializing Tell: ${(error as Error).message}`);
		isWaiting.value = false;
	}
});

onUnmounted(async () => {
	if (dataCapture.value) {
		await dataCapture.value.destroy();
		dataCapture.value = null;
	}

	eventBus.off(Events.INTERACTIVE_FORMS_UPDATE_REQUESTED, updateRequested);
})

</script>

<style scoped>
.ghostDraftView {
	height: 100% !important;
}

.datacapture-control-container {
	height: 100%;
	width: 100%;

	>iframe.tc_frame {
		height: 100% !important;
		width: 100% !important;
		min-width: var(--app-size-ghostdraft-min-w) !important;
		max-width: 100% !important;
	}
}
</style>